/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ---------------------------------------------------------------------------------------------
 */

import React, { Component } from "react";
import { Chart } from "chart.js";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { createMessageFactory } from "./../../utils/i18n";
import { TeamsSpinner } from "./../../components/common/teams-spinner";
import { Doughnut } from "react-chartjs-2";
import {
  ITeamsThemeContextProps,
  connectTeamsComponent,
} from "msteams-ui-components-react";
import {
  ThemeStyle
} from "msteams-ui-components-react";
import { isInIframe, isInWebView, getOptionsFromWebView, RequestType } from "../../utils/iframe";
import getFirstFillfiledPromise from "../../utils/promise";
import { FontIcon } from "office-ui-fabric-react/lib/Icon";
import dataURItoBlob from "../../utils/dataUri";
import { FilePicker } from "react-file-picker";
import { ActionButton, IIconProps, KeyCodes } from "office-ui-fabric-react";
import { teamsAppValidatorStyles } from "./styles";
import "./../icons.scss";
import openLinkThroughIframe from "../../utils/linkHelper";
import { sendTelemetry } from '../../utils/sendTelemetry';
import { IValidationTelemetryProperties, ValidationConstants, IValidationWarning, IValidatoinError } from '../../utils/ITelemetry';

const OMEX_ENDPOINT: string =
  "https://packageacceptance.omex.office.net/api/check?culture=en&mode=verifyandextract&packageType=msteams&verbose=true";

const msg = createMessageFactory("teams-app-validator");

interface IEnvironment {
  label: string;
  value: string;
  index: number;
}

interface AppProps {
  result: any;
  fileInfo: string;
  onResultChanged: any;
  onFileInfoChanged: any;
}

interface AppState {
  result: any;
  isLoading: boolean;
  isConfigsLoading: boolean;
  currentEnvironment: IEnvironment;
  environmentOptions: IEnvironment[];
  styles: any;
  fileInfo: string;
  selectedPackageUploadType: string;
}

declare global {
  interface Window {
    blobData?: string;
  }
}

type Props = ITeamsThemeContextProps & AppProps & InjectedIntlProps;

const chooseSelectEnvironment = 'choose-select-environment';

class TeamsAppValidator extends Component<Props, AppState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      result: this.props.result ? this.props.result : null,
      isLoading: false,
      isConfigsLoading: true,
      currentEnvironment: { label: "", value: "", index: 0 },
      environmentOptions: [],
      styles: this.styles(),
      fileInfo: this.props.fileInfo ? this.props.fileInfo : "",
      selectedPackageUploadType: chooseSelectEnvironment,
    };
  }

  private getConfigurations() {
    this.setState({ isConfigsLoading: true }, () => {
      window.parent.postMessage({ message: "getAppConfigurations" }, API_TOKEN_RESOURCE);

      let promises = [];
      promises.push(
        new Promise((res, _rej) =>
          setTimeout(() => res("Timeout response exceed 3000 ms"), 3000)
        )
      );
      promises.push(
        new Promise((resolveServicePromise) => {
          let displayMessage = (event: any) => {
            if (event.origin !== origin) {
              let configurations = event.data.configurations;

              resolveServicePromise(configurations);
            }
          };

          if (window.addEventListener) {
            // For standards-compliant web browsers
            window.addEventListener("message", displayMessage, false);
          }
        })
      );

      getFirstFillfiledPromise(promises).then((data: any) => {
        if (data === "Timeout response exceed 3000 ms") {
          this.setState({
            isConfigsLoading: false,
          });
          return;
        }

        if (data.length > 0) {
          let options = [];
          for (let i = 0; i < data.length; i++) {
            options.push({
              label: data[i].name,
              value: data[i].configuration + `#` + i,
              index: i
            });
          }

          this.setState({
            isConfigsLoading: false,
            currentEnvironment: options[0],
            environmentOptions: options,
          });

          // Run the first one automatically through the validation tool
          let blob = dataURItoBlob(data[0].configuration);
          this.sendValidationRequest(blob);
        } else {
          this.setState({
            isConfigsLoading: false,
          });
        }
      });
    });
  }

  private styles = () => {
    const themedColors = this.colors();

    return teamsAppValidatorStyles(themedColors);
  };

  private colors = () => {
    const { context } = this.props;

    switch (context.style) {
      case ThemeStyle.HighContrast:
        return {
          focus: context.colors.highContrast.white,
          shadowColor: "transparent",
        };
      case ThemeStyle.Dark:
        return {
          mainTextColor: "rgb(200, 200, 200)",
          dividerColor: "rgb(63, 63, 70)",
          panelHeaderText: "#fff",
          panelFileUploadBackground: "rgb(51, 51, 51)",
          panelFileUploadShadowBackground: "none",
          panelFileUploadLabelColor: "rgb(200, 200, 200)",
          panelValidationResultBackground: "rgb(45, 45, 48)",
          panelValidationResultPrintColor: "#A6A7DC",
          panelResultFixItLinkColor: "#A6A7DC",
          validationResultsSpinnerColor: "rgb(200, 200, 200)",
          panelInfoColContentColor: "rgb(241, 241, 241)",
          panelResultColumnColor: "rgb(200, 200, 200)",
          panelResultEvenRowBackground: "rgb(45, 45, 48)",
          panelResultOddRowBackground: "rgb(45, 45, 48)",
        };
      case ThemeStyle.Light:
      default:
        return {
          mainTextColor: "rgb(74, 74, 74)",
          dividerColor: "rgb(238, 238, 242)",
          panelHeaderText: "rgb(37, 37, 37)",
          panelFileUploadBackground: "rgb(245, 245, 245)",
          panelFileUploadShadowBackground:
            "0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13), 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11)",
          panelFileUploadLabelColor: "rgb(43, 44, 44)",
          panelValidationResultBackground: "rgb(255, 255, 255)",
          panelValidationResultPrintColor: "rgb(0, 122, 204)",
          panelResultFixItLinkColor: "rgb(0, 122, 204)",
          validationResultsSpinnerColor: "rgb(43, 44, 44)",
          panelInfoColContentColor: "rgb(34, 34, 34)",
          panelResultColumnColor: "rgb(34, 34, 34)",
          panelResultEvenRowBackground: "rgb(245, 245, 245)",
          panelResultOddRowBackground: "rgb(255, 255, 255)",
        };
    }
  };

  private handleUploadFile = (file: any) => {
    var reader = new FileReader();
    let that = this;
    reader.onload = function () {
      var dataUri = this.result;
      let blob = dataURItoBlob(dataUri);
      that.sendValidationRequest(blob);
    };
    reader.readAsDataURL(file);

    this.setState({
      fileInfo: file.name,
    });
    this.props.onFileInfoChanged(file.name);
  };

  /*
  curl --header "Content-Type: application/zip"
  --data-binary "@c:\projects\teams-devapp\manifest\int.zip"
  "https://packageacceptance.omex.office.net/api/check?culture=en&mode=verifyandextract&packageType=msteams&verbose=true"
  -i
  */
  private sendValidationRequest = (file: any) => {
    const http_request = new XMLHttpRequest();
    http_request.open("POST", OMEX_ENDPOINT);
    http_request.setRequestHeader("Content-Type", "application/zip");
    this.setState({ isLoading: true }, () => {
      http_request.send(file);

      http_request.onreadystatechange = () => {
        if (http_request.responseText) {
          let testResult = JSON.parse(http_request.responseText)
          this.setState({
            isLoading: false,
            result: testResult
          });

          let validationCompleteProperties: IValidationTelemetryProperties = {
            tool: ValidationConstants.tool,
            event: ValidationConstants.onValidationCompletion,
            telemetryData: {
              manifestVersion: testResult.addInDetails.manifestVersion,
              warningsID: testResult.warnings.map((warning: IValidationWarning) => warning.id),
              errorsID: testResult.errors.map((error: IValidatoinError) => error.id)
            }

          }
          sendTelemetry(ValidationConstants.message, validationCompleteProperties)

        }
        this.props.onResultChanged(JSON.parse(http_request.responseText));
      };
    });
  };

  public componentDidUpdate(prevProps: any) {
    if (this.props.context.style !== prevProps.context.style) {
      this.setState({ styles: this.styles() });
    }
  }

  public componentDidMount() {
    if (isInIframe(RequestType.VScode)) {
      this.getConfigurations();
    } else if (isInWebView(RequestType.VS)) {
      var blobDataFromWebView = window.blobData!;
      if (blobDataFromWebView) {
        this.loadAppValidationInWebView();
      }
    }
  }



  private loadAppValidationInWebView() {
    let options = getOptionsFromWebView();
    this.setState({
      isConfigsLoading: false,
      currentEnvironment: options[0],
      environmentOptions: options,
    });
    var datauriContent = options[0].value.split('#')[0];
    var publishBlob = dataURItoBlob(datauriContent);
    this.sendValidationRequest(publishBlob);
  }

  public render(): JSX.Element {
    const { intl } = this.props;

    const zipFolderIcon: IIconProps = { iconName: "ZipFolder" };
    const uploadZipIcon: IIconProps = { iconName: "Upload" };
    let themedColors = this.colors();

    let options: any = {
      tooltips: false,
      legend: {
        position: "right",
        labels: {
          boxWidth: 12,
          fontColor: themedColors.mainTextColor,
          generateLabels: function (chart: any) {
            let data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: any, i: number) => {
                let meta = chart.getDatasetMeta(0);
                let ds = data.datasets[0];
                let arc = meta.data[i];
                let custom = (arc && arc.custom) || {};
                let getValueAtIndexOrDefault =
                  Chart.helpers.getValueAtIndexOrDefault;
                let arcOpts = chart.options.elements.arc;
                let fill = custom.backgroundColor
                  ? custom.backgroundColor
                  : getValueAtIndexOrDefault(
                    ds.backgroundColor,
                    i,
                    arcOpts.backgroundColor
                  );
                let stroke = custom.borderColor
                  ? custom.borderColor
                  : getValueAtIndexOrDefault(
                    ds.borderColor,
                    i,
                    arcOpts.borderColor
                  );

                // We get the value of the current label
                let value =
                  chart.config.data.datasets[arc._datasetIndex].data[
                  arc._index
                  ];

                return {
                  // Instead of `text: label,`
                  // We add the value to the string
                  text: `${label} (${value})`,
                  fillStyle: fill,
                  strokeStyle: stroke,
                  lineWidth: 0,
                  hidden: isNaN(ds.data[i]) || meta.data[i].hidden,
                  index: i,
                };
              });
            } else {
              return [];
            }
          },
        },
      },
    };

    let data = {
      datasets: [
        {
          backgroundColor: ["green", "red", "orange"],
          borderWidth: 0,
          hoverBorderWidth: 0,
          hoverBackgroundColor: ["green", "red", "orange"],
          data: [
            this.state.result &&
            this.state.result.notes.filter(
              (elem: any) => elem.content && elem.content.length > 0
            ).length,
            this.state.result &&
            this.state.result.errors.filter(
              (elem: any) => elem.content && elem.content.length > 0
            ).length,
            this.state.result &&
            this.state.result.warnings.filter(
              (elem: any) => elem.content && elem.content.length > 0
            ).length,
          ],
        },
      ],

      // These labels appear in the legend and in the tooltips when hovering different arcs
      labels: ["Success", "Error", "Warning"],
    };

    const styles = this.state.styles;

    return (
      <div>
        <div className={styles.tabs.uploadManifest.common}>
          <div className={styles.tabs.uploadManifest.uploadForm.common}>
              <div className={styles.tabs.uploadManifest.uploadForm.col}>
                <FilePicker tabIndex={0}
                  extensions={["zip"]}
                  onChange={this.handleUploadFile}
                  onError={(errMsg: any) => {
                    this.setState({
                      fileInfo: errMsg,
                    });
                    this.props.onFileInfoChanged(errMsg);
                  }}
                >
                  <ActionButton
                    aria-label={intl.formatMessage(
                      msg({
                        id: "app-validation-upload-package-manifest",
                        defaultMessage: "Upload manifest package",
                      })
                    )}
                    className={styles.tabs.uploadManifest.uploadForm.inputFile}
                    iconProps={this.state.fileInfo ? zipFolderIcon : uploadZipIcon}
                  >
                    {this.state.fileInfo ? (this.state.fileInfo) : "Upload manifest package"}
                  </ActionButton>
                </FilePicker>
              </div>
          </div>
        </div>
        <div
          style={{ display: this.state.isLoading === true ? "block" : "none" }}
        >
          <TeamsSpinner
            className={styles.tabs.validationResults.spinner}
            small
            text="Processing..."
          />
        </div>
        <div
          id="validation-results"
          className={styles.tabs.validationResults.common}
          style={{
            display:
              this.state.result !== null && this.state.isLoading == false
                ? "block"
                : "none",
          }}
        >
          <div className={styles.tabs.validationResults.boxHeader.common}>
            <span className={styles.tabs.validationResults.boxHeader.title}>
              {this.getStatus() === "Accepted" ? (
                <div className="img-success big"></div>
              ) : (
                  <div className="img-error big"></div>
                )}
              <div>
                {this.getStatus() === "Accepted" ? (
                  <FormattedMessage
                    {...msg({
                      id: "app-validation-validate-passed",
                      defaultMessage: `Test passed`,
                    })}
                  />
                ) : (
                    <FormattedMessage
                      {...msg({
                        id: "app-validation-validate-failed",
                        defaultMessage: `Test failed`,
                      })}
                    />
                  )}
              </div>
            </span>
            {
              isInWebView(RequestType.VS) || isInIframe(RequestType.VScode)
                ? "" // hide "Print" button for VS and VSCode extensions due to limitation of print funcionality in iframe
                : <span role="link" tabIndex={0}
                  id="btn-print"
                  aria-label={intl.formatMessage(
                    msg({
                      id: "app-validation-print-button",
                      defaultMessage: "Print validation result",
                    })
                  )}
                  className={styles.tabs.validationResults.boxHeader.print}
                  onClick={this.printPage}
                  onKeyDown={this.onEnterKeyDown}
                >
                  <FontIcon
                    style={{
                      fontSize: "16px",
                      height: "16px",
                      width: "16px",
                      margin: "0 5px",
                    }}
                    iconName="Print"
                  ></FontIcon>
                  <div>
                    <FormattedMessage
                      {...msg({
                        id: "app-validation-validate-btn-print",
                        defaultMessage: `Print`,
                      })}
                    />
                  </div>
                </span>
            }
          </div>

          <div className={styles.divider}></div>

          <div className={styles.tabs.validationResults.infoPanel.common}>
            <div className={styles.tabs.validationResults.infoPanel.col.common}>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-app-name",
                    defaultMessage: `App Name`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getAppName()}
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-package-name",
                    defaultMessage: `Package Name`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getPackageName()}
              </div>
            </div>

            <div className={styles.tabs.validationResults.infoPanel.col.common}>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-run-time",
                    defaultMessage: `Run time`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getRunTime()}
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-publisher",
                    defaultMessage: `Publisher`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getPublisher()}
              </div>
            </div>

            <div className={styles.tabs.validationResults.infoPanel.col.common}>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-version",
                    defaultMessage: `Version`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getVersion()}
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-manifest-version",
                    defaultMessage: `Manifest Version`,
                  })}
                />
              </div>
              <div
                className={styles.tabs.validationResults.infoPanel.col.content}
              >
                {this.getManifestVersion()}
              </div>
            </div>

            <div className={styles.tabs.validationResults.infoPanel.col.common}>
              <div
                className={styles.tabs.validationResults.infoPanel.col.header}
              >
                <FormattedMessage
                  {...msg({
                    id: "app-validation-validate-results-total-tests",
                    defaultMessage: `Total tests `,
                  })}
                />
                ({this.getTotalCount()})
              </div>
              <div
                style={{
                  position: "relative",
                  height: "80px",
                  width: "160px",
                }}
              >
                <Doughnut data={data} options={options} />
              </div>
            </div>
          </div>

          <div className={styles.divider}></div>

          <div className={styles.tabs.validationResults.result.common}>
            <div className={styles.tabs.validationResults.result.rowHeader}>
              <div
                className={styles.tabs.validationResults.result.row.colFirst}
              >
                <div
                  className={styles.tabs.validationResults.result.row.header}
                >
                  <FormattedMessage
                    {...msg({
                      id: "app-validation-validate-results-description",
                      defaultMessage: `Description`,
                    })}
                  />
                </div>
              </div>
            </div>
            {this.getValidationTests(
              styles,
              this.state.result ? this.state.result.errors : [],
              "error"
            )}
            {this.getValidationTests(
              styles,
              this.state.result ? this.state.result.warnings : [],
              "warning"
            )}
            {this.getValidationTests(
              styles,
              this.state.result ? this.state.result.notes : [],
              "success"
            )}
          </div>
        </div>
      </div>
    );
  }

  private onEnterKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.keyCode === KeyCodes.enter) {
      this.printPage();
    }
  }

  private getAppName(): React.ReactNode {
    return this.state.result && this.state.result.addInDetails
      ? `${this.state.result.addInDetails.displayName}`
      : "";
  }

  private getPackageName(): React.ReactNode {
    return this.state.result && this.state.result.addInDetails
      ? `${this.state.result.addInDetails.packageName}`
      : "";
  }

  private getRunTime(): React.ReactNode {
    if (this.state.result === null) {
      return;
    }

    const d: Date = new Date(this.state.result.utcTimestamp);

    return `${
      d.getMonth() + 1
      }/${d.getDate()}/${d.getFullYear()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
  }

  private getPublisher(): React.ReactNode {
    return this.state.result && this.state.result.addInDetails
      ? `${this.state.result.addInDetails.developerName}`
      : "";
  }

  private getVersion(): React.ReactNode {
    return this.state.result && this.state.result.addInDetails
      ? `${this.state.result.addInDetails.version}`
      : "";
  }

  private getManifestVersion(): React.ReactNode {
    return this.state.result && this.state.result.addInDetails
      ? `${this.state.result.addInDetails.manifestVersion}`
      : "";
  }

  private getTotalCount() {
    return (
      this.state.result &&
      `${
      this.state.result.notes.filter(
        (elem: any) => elem.content && elem.content.length > 0
      ).length +
      this.state.result.warnings.filter(
        (elem: any) => elem.content && elem.content.length > 0
      ).length +
      this.state.result.errors.filter(
        (elem: any) => elem.content && elem.content.length > 0
      ).length
      }`
    );
  }

  private getStatus(): React.ReactNode {
    return this.state.result && this.state.result.status;
  }

  private printPage() {
    var printButton = document.getElementById("btn-print");
    if (printButton) {
      printButton.style.visibility = "hidden";
      //Print the page content
      window.print();
      printButton.style.visibility = "visible";
    }
  }

  private getValidationTests(styles: any, testArr: any[], testType: string) {
    return testArr.map((elem: any) => {
      if (elem.content && elem.content.length > 0) {
        return (
          <div
            className={styles.tabs.validationResults.result.row.common}
            key={`${Math.random().toPrecision()}`}
          >
            <div className={styles.tabs.validationResults.result.row.colFirst}>
              <div className={"img-" + testType}></div>
              <div>
                {
                  // check if we've got exceptions not validation tests
                  elem.line && elem.line >= 0 && elem.column && elem.column >= 0
                    ? `${elem.title}: ${elem.content} (see ${elem.filePath} line:${elem.line} column:${elem.column})`
                    : elem.content
                }
                &nbsp;
                {
                  // add "Learn to fix it" link in case of error/warning test
                  testType !== `success` ? this.getFixItLinks(elem, styles) : ""
                }
              </div>
            </div>
          </div>
        );
      } else {
        return;
      }
    });
  }

  private getFixItLinks(elem: any, styles: any) {
    let templateLink =
      "https://docs.microsoft.com/en-us/microsoftteams/platform/resources/schema/manifest-schema";
    let link = "";
    if (elem.helpUrl && elem.helpUrl.length > 0) {
      link = elem.helpUrl;
    } else {
      //generate link in case we've got exceptions validation errors
      if (elem.title) {
        let parentProp = elem.title.split(".")[0];
        parentProp = parentProp.split("[")[0].toLowerCase();
        link = `${templateLink}#${parentProp}`;
      } else {
        link = templateLink;
      }
    }

    let fixItLinksTelemetryProperties: IValidationTelemetryProperties = {
      tool: ValidationConstants.tool,
      event: ValidationConstants.onHelpLinkClick,
      telemetryData: {
        fixItLink: link
      }
    }

    return (
      <a aria-label={`learn to fix ${elem.content}`}
        className={styles.tabs.validationResults.result.row.fixItLink}
        target="_blank"
        onClick={() => {
          openLinkThroughIframe(link);
          sendTelemetry(ValidationConstants.message, fixItLinksTelemetryProperties)
        }}
        href={link}
      >
        <FormattedMessage
          {...msg({
            id: "app-validation-learn-to-fix-it-link",
            defaultMessage: `Learn to fix it`,
          })}
        />

      </a>
    );
  }
}

export default injectIntl(connectTeamsComponent(TeamsAppValidator));
